import { useState, type ReactNode } from "react";
import type { Developer } from "../auth";
import type { ManagementPermission } from "../api";
import { APP_VERSION } from "../config";
import { initials } from "../presentation";
import { Brand } from "./Brand";
import { Button, IconButton } from "./ui/Button";
import { Icon, type IconName } from "./ui/Icon";

export type ConsoleView = "applications" | "users" | "system-accounts" | "groups" |
  "server-messages" | "callbacks" | "callback-failures" | "quickstart" | "settings";

interface ConsoleShellProps {
  readonly children: ReactNode;
  readonly developer: Developer | null;
  readonly view: ConsoleView;
  readonly navigationOpen: boolean;
  readonly onChangeView: (view: ConsoleView) => void;
  readonly onCloseNavigation: () => void;
  readonly onOpenNavigation: () => void;
  readonly onLogout: () => void;
  readonly permissions: readonly ManagementPermission[];
}

const navigation: readonly {
  readonly id: string;
  readonly label: string;
  readonly items: readonly {
    readonly view: ConsoleView;
    readonly icon: IconName;
    readonly label: string;
    readonly description: string;
    readonly permission?: ManagementPermission;
  }[];
}[] = [
  { id: "console", label: "控制台", items: [{ view: "applications", icon: "apps", label: "应用管理", description: "应用与接入凭证" }] },
  { id: "accounts", label: "账号管理", items: [
    { view: "users", icon: "users", label: "IM 用户", description: "用户目录与创建", permission: "im.user.read" },
    { view: "system-accounts", icon: "server", label: "系统账号", description: "服务端消息身份", permission: "im.system_account.read" },
  ] },
  { id: "operations", label: "运营管理", items: [
    { view: "groups", icon: "groups", label: "群组管理", description: "群组与成员概览", permission: "im.group.read" },
  ] },
  { id: "developer-tools", label: "开发者工具", items: [
    { view: "server-messages", icon: "server", label: "服务端消息", description: "可信服务端发送记录", permission: "im.message.read" },
    { view: "quickstart", icon: "code", label: "快速接入", description: "SDK 与示例代码" },
  ] },
  { id: "developer-config", label: "开发配置", items: [
    { view: "callbacks", icon: "settings", label: "回调配置", description: "事件、签名与投递策略", permission: "im.callback.read" },
    { view: "callback-failures", icon: "warning", label: "回调错误日志", description: "失败、重试与死信诊断", permission: "im.callback_failure_log.read" },
  ] },
  { id: "settings", label: "设置", items: [{ view: "settings", icon: "settings", label: "平台配置", description: "端点与资源边界" }] },
];

const titles: Record<ConsoleView, { readonly title: string; readonly subtitle: string }> = {
  applications: { title: "应用管理", subtitle: "注册、配置和管理接入 Sonox IM 的应用" },
  users: { title: "用户管理", subtitle: "查询和创建当前应用的 IM 用户" },
  "system-accounts": { title: "系统账号", subtitle: "配置应用级服务端单聊、群聊与广播发送身份" },
  groups: { title: "群组管理", subtitle: "查询和创建当前应用的群组" },
  "server-messages": { title: "服务端消息", subtitle: "审查可信服务端 API 的消息发送记录" },
  callbacks: { title: "回调配置", subtitle: "管理消息审核、消息通知与用户状态回调" },
  "callback-failures": { title: "回调错误日志", subtitle: "定位回调拒绝、异常、重试与死信" },
  quickstart: { title: "快速接入", subtitle: "从身份认证到实时消息的完整接入路径" },
  settings: { title: "平台配置", subtitle: "查看公共端点、资源边界和运行状态" },
};

/** Stable SaaS shell that separates navigation chrome from feature content. */
export function ConsoleShell({
  children,
  developer,
  view,
  navigationOpen,
  onChangeView,
  onCloseNavigation,
  onOpenNavigation,
  onLogout,
  permissions,
}: ConsoleShellProps) {
  const page = titles[view];
  // 导航默认全部折叠，避免多业务域同时展开造成侧栏信息噪声。展开状态
  // 只属于当前控制台会话，不写入浏览器存储，也不会改变业务路由。
  const [expandedSections, setExpandedSections] = useState<ReadonlySet<string>>(() => new Set());

  function toggleSection(sectionId: string): void {
    setExpandedSections((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  return (
    <div className="console-shell">
      <button
        aria-label="关闭导航"
        className={`sidebar-scrim ${navigationOpen ? "visible" : ""}`}
        type="button"
        onClick={onCloseNavigation}
      />
      <aside className={`sidebar ${navigationOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-brand"><Brand inverse /></div>
        <div className="workspace-switcher" aria-label="当前工作区">
          <span className="workspace-icon">IM</span>
          <span><small>当前工作区</small><strong>Developer Console</strong></span>
          <Icon name="chevron-down" size={15} />
        </div>
        <nav className="nav" aria-label="开放平台导航">
          {navigation.map((section) => {
            const items = section.items.filter(
              (item) => item.permission === undefined || permissions.includes(item.permission),
            );
            if (items.length === 0) return null;
            const expanded = expandedSections.has(section.id);
            const containsCurrentView = items.some((item) => item.view === view);
            const submenuId = `nav-submenu-${section.id}`;
            return <div className="nav-section" key={section.id}>
              <button
                aria-controls={submenuId}
                aria-expanded={expanded}
                className={`nav-parent ${containsCurrentView ? "contains-active" : ""}`}
                type="button"
                onClick={() => { toggleSection(section.id); }}
              >
                <span>{section.label}</span>
                <Icon className="nav-parent-chevron" name="chevron-down" size={15} />
              </button>
              <div className="nav-submenu" hidden={!expanded} id={submenuId}>
                {items.map((item) => (
                  <button
                    aria-current={view === item.view ? "page" : undefined}
                    className={`nav-item ${view === item.view ? "active" : ""}`}
                    key={item.view}
                    type="button"
                    onClick={() => {
                      onChangeView(item.view);
                      onCloseNavigation();
                    }}
                  >
                    <Icon name={item.icon} />
                    <span><strong>{item.label}</strong><small>{item.description}</small></span>
                  </button>
                ))}
              </div>
            </div>;
          })}
        </nav>
        <div className="service-status">
          <div><span className="service-dot" /><strong>服务运行正常</strong></div>
          <p>数据面维持 3 个分片</p>
          <small>Console v{APP_VERSION}</small>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <div className="topbar-title">
            <IconButton className="mobile-menu-button" icon="menu" label="打开导航" onClick={onOpenNavigation} />
            <div><h1>{page.title}</h1><p>{page.subtitle}</p></div>
          </div>
          <div className="topbar-actions">
            <button className="documentation-link" type="button" onClick={() => { onChangeView("quickstart"); }}>
              <Icon name="book" size={16} />接入文档
            </button>
            <span className="topbar-divider" />
            <span className="avatar">{initials(developer?.name)}</span>
            <span className="user-summary">
              <strong>{developer?.name ?? "开发者"}</strong>
              <small>{developer?.email ?? "Sonox Account"}</small>
            </span>
            <Button leadingIcon="logout" size="small" variant="ghost" onClick={onLogout}>退出</Button>
          </div>
        </header>
        <div className="content">{children}</div>
      </main>
    </div>
  );
}
